import { useState, Suspense } from 'react';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Tooltip,
  Menu,
  MenuItem,
} from '@mui/material';
import DarkModeOutlinedIcon from '@mui/icons-material/DarkModeOutlined';
import LightModeOutlinedIcon from '@mui/icons-material/LightModeOutlined';
import LanguageIcon from '@mui/icons-material/Language';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined';
import { Outlet } from 'react-router-dom';
import { motion } from 'framer-motion';
import { AppLogo } from '@/components/brand/AppLogo';
import { ConnectedCommunityIllustration } from '@/components/brand/ConnectedCommunityIllustration';
import { AppBodyLoader } from '@/components/common/AppLoader';
import { useThemeMode } from '@/contexts/ThemeModeContext';
import { useTranslation } from '@/i18n';

export function AuthLayout() {
  const { mode, toggleTheme } = useThemeMode();
  const { t, language, setLanguage, languages, currentLanguage } = useTranslation();
  const [languageAnchorEl, setLanguageAnchorEl] = useState<null | HTMLElement>(null);

  return (
    <Box
      id="login-container"
      data-testid="login-container"
      sx={{
        minHeight: '100vh',
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: '52% 48%' },
        width: '100%',
        maxWidth: '100%',
        overflowX: 'hidden',
        minWidth: 0,
      }}
    >
      {/* Left Branding Panel (Desktop Only) */}
      <Box
        sx={{
          display: { xs: 'none', md: 'flex' },
          flexDirection: 'column',
          justifyContent: 'space-between',
          p: { md: 5, lg: 6 },
          color: '#F8FAFC',
          background:
            'linear-gradient(155deg, #0D1328 0%, #14213D 48%, #008F78 135%)',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Background Ambient Blobs */}
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            background:
              'radial-gradient(circle at 15% 15%, rgba(0, 184, 148, 0.16), transparent 45%), radial-gradient(circle at 85% 85%, rgba(56, 189, 248, 0.15), transparent 40%)',
            pointerEvents: 'none',
          }}
        />

        {/* Top Header Block */}
        <Box sx={{ position: 'relative', zIndex: 2 }}>
          <AppLogo onDark sx={{ maxWidth: 210, mb: 3 }} />
          <Typography
            variant="h4"
            sx={{
              fontWeight: 800,
              letterSpacing: '-0.03em',
              lineHeight: 1.25,
              maxWidth: 480,
              fontSize: { md: '1.9rem', lg: '2.25rem' },
              color: '#FFFFFF',
            }}
          >
            Connected communities. Simplified.
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: 'rgba(248, 250, 252, 0.82)',
              maxWidth: 460,
              mt: 1.5,
              fontSize: '1rem',
              lineHeight: 1.6,
            }}
          >
            Payments, visitors, complaints, events and resident communication — connected in one workspace.
          </Typography>
        </Box>

        {/* Center Animated Connected Community Illustration */}
        <Box sx={{ position: 'relative', zIndex: 2, my: 'auto', width: '100%' }}>
          <ConnectedCommunityIllustration />
        </Box>

        {/* Bottom Trust & Security Indicator */}
        <Box
          sx={{
            position: 'relative',
            zIndex: 2,
            display: 'flex',
            alignItems: 'center',
            gap: 1.25,
            pt: 2,
            borderTop: '1px solid rgba(255, 255, 255, 0.08)',
          }}
        >
          <VerifiedUserOutlinedIcon sx={{ fontSize: 18, color: '#00B894' }} />
          <Typography variant="caption" sx={{ color: 'rgba(248, 250, 252, 0.72)', fontWeight: 600 }}>
            Enterprise Security · 256-bit Encryption · ISO 27001 Compliant Architecture
          </Typography>
        </Box>
      </Box>

      {/* Right Form Container (Desktop & Mobile) */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100dvh',
          p: { xs: 2, sm: 4 },
          pt: { xs: 'calc(60px + var(--safe-area-top, 0px))', sm: 5 },
          pb: { xs: 'calc(32px + var(--safe-area-bottom, 0px))', sm: 5 },
          position: 'relative',
          bgcolor: mode === 'dark' ? '#0B0F19' : '#F5F7FC',
          backgroundImage:
            mode === 'dark'
              ? 'radial-gradient(at 50% 0%, rgba(0, 184, 148, 0.08) 0px, transparent 60%)'
              : 'radial-gradient(at 50% 0%, rgba(0, 184, 148, 0.06) 0px, transparent 65%), radial-gradient(at 100% 100%, rgba(20, 33, 61, 0.03) 0px, transparent 50%)',
        }}
      >
        {/* Floating Language and Theme Switcher in Top-Right */}
        <Box
          sx={{
            position: 'absolute',
            top: 'calc(16px + var(--safe-area-top, 0px))',
            right: 24,
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
            bgcolor: mode === 'dark' ? 'rgba(21, 29, 44, 0.9)' : 'rgba(255, 255, 255, 0.9)',
            backdropFilter: 'blur(12px)',
            border: '1px solid',
            borderColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(226, 231, 239, 0.9)',
            borderRadius: '12px',
            p: 0.5,
            boxShadow: '0 4px 16px rgba(20, 33, 61, 0.06)',
            zIndex: 10,
          }}
        >
          <Tooltip title={t('common.selectLanguage')}>
            <IconButton
              onClick={(e) => setLanguageAnchorEl(e.currentTarget)}
              aria-label={t('common.selectLanguage')}
              size="small"
              sx={{
                minWidth: 44,
                minHeight: 44,
                borderRadius: '8px',
                px: 1,
                gap: 0.5,
                color: 'text.secondary',
              }}
            >
              <LanguageIcon fontSize="small" />
              <Box component="span" sx={{ fontSize: '0.78rem', fontWeight: 700 }}>
                {currentLanguage.badge || language.toUpperCase()}
              </Box>
            </IconButton>
          </Tooltip>
          <Menu
            anchorEl={languageAnchorEl}
            open={Boolean(languageAnchorEl)}
            onClose={() => setLanguageAnchorEl(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          >
            {languages.map((lang) => (
              <MenuItem
                key={lang.code}
                selected={language === lang.code}
                onClick={() => {
                  setLanguage(lang.code);
                  setLanguageAnchorEl(null);
                }}
                sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, minWidth: 160 }}
              >
                <Box>
                  <Typography variant="body2" fontWeight={language === lang.code ? 700 : 500}>
                    {lang.nativeName}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {lang.name}
                  </Typography>
                </Box>
                {language === lang.code ? <CheckCircleIcon color="primary" sx={{ fontSize: 16 }} /> : null}
              </MenuItem>
            ))}
          </Menu>

          <Tooltip title={mode === 'dark' ? t('common.lightMode') : t('common.darkMode')}>
            <IconButton
              onClick={toggleTheme}
              aria-label={mode === 'dark' ? t('common.lightMode') : t('common.darkMode')}
              size="small"
              sx={{
                minWidth: 44,
                minHeight: 44,
                borderRadius: '8px',
              }}
            >
              {mode === 'dark' ? <LightModeOutlinedIcon fontSize="small" /> : <DarkModeOutlinedIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
        </Box>

        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
          style={{ width: '100%', maxWidth: 460 }}
        >
          <Paper
            elevation={0}
            sx={{
              p: { xs: 3, sm: '36px 40px' },
              borderRadius: '24px',
              border: '1px solid',
              borderColor: mode === 'dark' ? 'rgba(255,255,255,0.08)' : '#E2E7EF',
              bgcolor: mode === 'dark' ? '#111827' : '#FFFFFF',
              boxShadow:
                mode === 'dark'
                  ? '0 20px 60px rgba(0, 0, 0, 0.45)'
                  : '0 16px 40px rgba(20, 33, 61, 0.06)',
            }}
          >
            {/* Mobile Header Logo */}
            <Box
              sx={{
                display: { xs: 'flex', md: 'none' },
                justifyContent: 'center',
                alignItems: 'center',
                mb: 3,
                pt: 0.5,
              }}
            >
              <AppLogo sx={{ maxWidth: 190, height: 44, objectFit: 'contain' }} />
            </Box>
            <Suspense fallback={<AppBodyLoader />}>
              <Outlet />
            </Suspense>
          </Paper>
        </motion.div>
      </Box>
    </Box>
  );
}

export default AuthLayout;
